import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import "./Header.css";
import { Button } from "./ui/Button";

import baseballIcon from "../assets/brand/baseball.svg";
import playerIcon from "../assets/brand/player.svg";
import appleIcon from "../assets/external/apple.svg";
import GlobalSearch from "./common/GlobalSearch.js";

export const setHeaderTitle = (title) => {
  const event = new CustomEvent("header-title-change", { detail: title });
  globalThis.dispatchEvent(event);
};

const Header = ({ user, onSignOut, onSignIn }) => {
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    if (showProfileMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showProfileMenu]);

  const renderAvatar = () => {
    if (user?.photoUrl) {
      return <img src={user.photoUrl} alt="User" />;
    }
    if (
      user?.name &&
      user.name.trim() !== "" &&
      user.name.trim() !== "User" &&
      user.name.trim() !== "Unknown"
    ) {
      const parts = user.name.split(" ").filter((p) => p.trim() !== "");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return parts[0][0].toUpperCase();
    }
    if (user?.email) {
      const emailUser = user.email.split("@")[0];
      const parts = emailUser.split(/[._-]/).filter(Boolean);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return user.email[0].toUpperCase();
    }
    return "U";
  };

  const resolvedDisplayName =
    user?.name && user.name !== "User" && user.name !== "Unknown"
      ? user.name
      : user?.email || "Profile";

  return (
    <header className="app-header">
      <div className="header-left">
        <button
          type="button"
          className="plain-btn header-logo-btn"
          onClick={() => navigate("/")}
          aria-label="Home"
        >
          <img src={baseballIcon} alt="Play Ball" className="logo-icon" />
        </button>
      </div>

      <div className="header-center">
        <GlobalSearch />
      </div>

      <div className="header-right">
        <div className="user-menu" ref={menuRef}>
          <button
            type="button"
            className="user-profile-btn profile-trigger"
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            aria-label={user ? "User Menu" : "Guest Menu"}
          >
            <div className="avatar-circle">
              {user ? (
                renderAvatar()
              ) : (
                <img
                  src={playerIcon}
                  alt="Guest"
                  style={{
                    width: "18px",
                    height: "18px",
                    objectFit: "contain",
                  }}
                />
              )}
            </div>
          </button>

          {showProfileMenu && (
            <div className="profile-menu fade-in">
              {user ? (
                <>
                  <div className="profile-menu-header">
                    <div className="profile-menu-name">
                      {resolvedDisplayName}
                    </div>
                    {user.email && (
                      <div className="profile-menu-email">{user.email}</div>
                    )}
                  </div>
                  <div className="profile-menu-actions">
                    <Button
                      variant="destructive"
                      type="button"
                      style={{ width: "100%" }}
                      onClick={() => {
                        setShowProfileMenu(false);
                        if (onSignOut) onSignOut();
                      }}
                    >
                      Sign Out
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="profile-menu-header">
                    <div className="profile-menu-name">Guest</div>
                    <div className="profile-menu-email">
                      Sign in to join leagues &amp; teams
                    </div>
                  </div>
                  <div className="profile-menu-actions">
                    <button
                      type="button"
                      className="apple-signin-sm"
                      onClick={() => {
                        setShowProfileMenu(false);
                        if (onSignIn) onSignIn();
                      }}
                    >
                      <img
                        src={appleIcon}
                        alt="Apple Logo"
                        style={{
                          width: "16px",
                          height: "16px",
                          filter: "var(--apple-logo-filter)",
                        }}
                      />
                      <span>Sign in with Apple</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

Header.propTypes = {
  user: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    photoUrl: PropTypes.string,
  }),
  onSignOut: PropTypes.func,
  onSignIn: PropTypes.func,
};

export default Header;
